import { FieldArray, Formik, useFormikContext } from "formik"
import {
    Autocomplete,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    TextField,
    Typography,
    useTheme
} from "@mui/material"
import { useApi } from "auth/useApi3"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useEffect, useState } from "react"
import { apiEndpoints } from "generated/apiEndpoints"
import { fileValidationSchema } from "pages/documents/components/folderValidationSchema"
import PaperBox from "components/PaperBox"
import FileFormList from "pages/documents/components/FileFormList"
import FormUploadFile from "components/Form/FormComponents/FormUploadFile/FormUploadFile"
import { useTranslation } from "react-i18next";

interface TitleLabelProps {
    label: string
}

const TitleLabel = ({ label }: TitleLabelProps) => {
    const theme = useTheme()
    return (
        <InputLabel sx={{ paddingBottom: '8px' }}>
            <Typography color={(theme.palette as any).default[900]} variant="labelLarge" fontWeight={500}>
                {label}
            </Typography>
        </InputLabel>
    )
}

interface StatusDropdownProps {
    id: number
    statusOptions: Array<{ value: number; label: string }>
}

interface DeviationFormInputs {
    involvedUserId?: number
    status: number
    priority: number
    assignedUserId: number
    notes?: string
    actions?: string
    reported?: boolean
}

export const StatusDropdown = ({ id, statusOptions }: StatusDropdownProps) => {
    const { values, setFieldValue, handleChange, handleBlur } = useFormikContext<DeviationFormInputs>()
    const theme = useTheme()
    const { t } = useTranslation("translation")

    return (
        <>
            <TitleLabel label={t("global.status", "Status")} />
            <FormControl fullWidth>
                <Autocomplete
                    sx={{ width: "100%", background: theme.palette.background.paper }}
                    options={statusOptions}
                    getOptionLabel={(option: any) => (option?.label ? option?.label : "")}
                    value={statusOptions?.find((x: any) => x.value === values.status) || null}
                    onChange={(e, option) => setFieldValue("status", option?.value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name={id?.toString()}
                            variant="outlined"
                            defaultValue={values.status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    )}
                />
            </FormControl>
        </>
    )
}

interface PriorityDropdownProps {
    id: number
    priorityOptions: Array<{ value: number; label: string }>
}

export const PriorityDropdown = ({ id, priorityOptions }: PriorityDropdownProps) => {
    const { values, setFieldValue, handleChange, handleBlur } = useFormikContext<DeviationFormInputs>()
    const theme = useTheme()
    const { t } = useTranslation("translation", { keyPrefix: "deviation" })

    return (
        <>
            <TitleLabel label={t("priority", "Priority")} />
            <FormControl fullWidth>
                <Autocomplete
                    sx={{ width: "100%", background: theme.palette.background.paper }}
                    options={priorityOptions}
                    getOptionLabel={(option: any) => option?.label}
                    value={priorityOptions?.find((x: any) => x.value === values.priority) || null}
                    onChange={(e, option) => setFieldValue("priority", option?.value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name={id?.toString()}
                            variant="outlined"
                            defaultValue={values.priority}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    )}
                />
            </FormControl>
        </>
    )
}

interface AssignDropdownProps {
    props: {
        id: number
        feature: string
        assignedUserId: number
        assignedUser: string
    }
}

interface AssignedUser {
    id: number
    name: string
}

export const AssignDropdown = ({ props }: AssignDropdownProps) => {
    const theme = useTheme()
    const { t } = useTranslation("translation", { keyPrefix: "deviation" })
    const [assignUsersResult, assignUserApi] = useApi() as [IApiResult, IApiFunc]
    const { values, setFieldValue, handleChange, handleBlur } = useFormikContext<DeviationFormInputs>()
    const [assignOptions, setAssignOptions] = useState<AssignedUser[]>([])

    useEffect(() => {
        assignUserApi.get(apiEndpoints.deviation.getusers.replace("{feature}", props.feature))
    }, [])


    useEffect(() => {
        if (assignUsersResult.status !== 2) return
        setAssignOptions(assignUsersResult.data)
        if (!assignOptions.find((user: AssignedUser) => user.id === props?.assignedUserId) && props?.assignedUserId) {
            setAssignOptions((prevState: AssignedUser[]) => [...prevState, { id: props?.assignedUserId, name: props?.assignedUser }])
        }
    }, [assignUsersResult])

    return (
        <>
            <TitleLabel label={t("assign", "Assign")} />
            <FormControl fullWidth>
                <Autocomplete
                    sx={{ width: "100%", background: theme.palette.background.paper }}
                    options={assignOptions}
                    getOptionLabel={(option: any) => `${option?.name}`}
                    value={assignOptions?.find((x: any) => x.id === values.assignedUserId) || { id: 0, name: "" }}
                    onChange={(e, option) => setFieldValue("assignedUserId", option?.id)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name={props?.id?.toString()}
                            variant="outlined"
                            defaultValue={values.assignedUserId || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    )}
                />
            </FormControl>
        </>
    )
}

export const NotesTextField = () => {
    const { values, handleChange } = useFormikContext<DeviationFormInputs>()
    const theme = useTheme()
    const { t } = useTranslation("translation", { keyPrefix: "deviation" })

    return (
        <>
            <TitleLabel label={t("notes", "Notes")} />
            <FormControl fullWidth>
                <TextField
                    id="notes"
                    onChange={handleChange}
                    value={values.notes}
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder={t("notes", "Notes")}
                    sx={{ width: "100%", background: theme.palette.background.paper }}
                />
            </FormControl>
        </>
    )
}

export const ActionsTextField = () => {
    const { values, handleChange } = useFormikContext<DeviationFormInputs>()
    const theme = useTheme()
    const { t } = useTranslation("translation", { keyPrefix: "deviation" })

    return (
        <>
            <TitleLabel label={t("actions", "Actions")} />
            <FormControl fullWidth>
                <TextField
                    id="actions"
                    onChange={handleChange}
                    value={values.actions}
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder={t("actions", "Actions")}
                    sx={{ width: "100%", background: theme.palette.background.paper }}
                />
            </FormControl>
        </>
    )
}

export const ReportedCheckbox = () => {
    const { values, setFieldValue } = useFormikContext<DeviationFormInputs>()
    const { t } = useTranslation("translation", { keyPrefix: "deviation" })

    return (
        <FormControlLabel
            label={t("reported_to_anmalarbetsskada", "Reported to anmalarbetsskada.se",)}
            control={<Checkbox
                checked={values.reported}
                color="primary"
                onChange={(_, checked) => setFieldValue("reported", checked)}
            />}
        />
    )
}

const AttachmentDropzone = ({ id }: { id: number }) => {
    const { values, setFieldValue } = useFormikContext<{ attachments: DeviationAttachment[] }>()
    const [blobUploadResult, blobUploadApi] = useApi() as [IApiResult, IApiFunc]
    const [deviationAttachmentUploadResult, deviationAttachmentUploadApi] = useApi() as [IApiResult, IApiFunc]
    const { t } = useTranslation()

    useEffect(() => {
        if (deviationAttachmentUploadResult.status !== 2) return
        setFieldValue("attachments", [
            ...values.attachments,
            { ...deviationAttachmentUploadResult.data, name: deviationAttachmentUploadResult.data.fileName }
        ])
    }, [deviationAttachmentUploadResult])

    useEffect(() => {
        if (blobUploadResult.status !== 2) return
        const url = apiEndpoints.deviation.upsertdeviationattachment.replace("{deviationId}", id.toString())
        blobUploadResult.data.forEach((file: any) => {
            deviationAttachmentUploadApi.put(url, file)
        })
    }, [blobUploadResult])

    function uploadBlobFiles(blobFiles: File[]) {
        const url = apiEndpoints.attachment.addattachment.replace("{feature}", "deviation")
        blobFiles.forEach(file => {
            blobUploadApi.file(url, file)
        })
    }

    return (
        <>
            <TitleLabel label={t("global.files_for_internal_use", "Files for internal use")} />
            <FormUploadFile
                handleChange={(blobFiles: File[]) => {
                    uploadBlobFiles(blobFiles)
                }}
            />
        </>
    )
}

interface DeviationAttachment {
    deviationId: number
    fileName: string
    fileId: string
}

export const AttachmentArea = ({ id }: { id: number }) => {
    const [deviationAttachmentResult, deviationAttachmentApi] = useApi() as [IApiResult, IApiFunc]
    const [upsertResult, upsertApi] = useApi() as [IApiResult, IApiFunc]
    const [targetFileIndex, setTargetFileIndex] = useState<null | any>(null)
    const { t } = useTranslation()

    useEffect(() => {
        fetchAttachments()
    }, [])

    useEffect(() => {
        if (upsertResult.status !== 2) return
        fetchAttachments()
    }, [upsertResult])

    function fetchAttachments() {
        const url = apiEndpoints.deviation.getdeviationattachmentsbydeviationid.replace("{deviationId}", id.toString())
        deviationAttachmentApi.get(url)
    }

    const upsertClickedAttachment = (valuesObj: any) => {
        const clickedAttachment = valuesObj?.attachments[targetFileIndex]
        if (targetFileIndex === null) return
        const attachmentData = {
            ...clickedAttachment,
            fileName: clickedAttachment.name
        }
        const url = apiEndpoints.deviation.upsertdeviationattachment.replace("{deviationId}", id.toString())
        upsertApi.put(url, attachmentData)
    }

    const attachments = deviationAttachmentResult?.data?.map((attachment: DeviationAttachment) => ({
        ...attachment,
        name: attachment.fileName,
    })) || []

    return (
        <>
            <Formik
                validationSchema={fileValidationSchema(t)}
                initialValues={{ attachments }}
                onSubmit={upsertClickedAttachment}
                enableReinitialize={true}>
                {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                    <form id="DeviationAttachmentForm" onSubmit={handleSubmit} noValidate>
                        <AttachmentDropzone id={id} />
                        {(values.attachments && values.attachments.length > 0) && (
                            <PaperBox>
                                <FieldArray
                                    name="attachments"
                                    render={() => (
                                        <FileFormList
                                            values={values}
                                            handleBlur={handleBlur}
                                            handleChange={handleChange}
                                            setFieldValue={setFieldValue}
                                            editFile={targetFileIndex}
                                            setEditFile={setTargetFileIndex}
                                            fileUpdateResult={upsertResult}
                                            downloadUrl={apiEndpoints.deviation.getdeviationattachment}
                                            deleteUrl={apiEndpoints.deviation.deletedeviationattachment}
                                        />
                                    )}
                                />
                            </PaperBox>
                        )}
                    </form>
                )}
            </Formik>
        </>
    )
}
